import Vue from 'vue';
import { FormCountdownDigitBackground, FormCountdownVO, PopSettingsVO } from '@/model/FormWheel';

/** digitBackground 扩展字段（与 FormCountdownDigitBackground.flipLineColor 对齐） */
type ExtCountdownDigitBackground = FormCountdownDigitBackground & { flipLineColor?: string };

function ensureDigitBackgroundFlipLineColor(bg: FormCountdownDigitBackground) {
  const dig = bg as ExtCountdownDigitBackground;
  if (dig.flipLineColor === undefined) Vue.set(dig, 'flipLineColor', '');
}
import { isEnableExtendedPopCountdown } from '@/utils/experiment';
import {
  EXT_COUNTDOWN_FIXED_DURATION_DEFAULT_SECONDS,
  EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS,
  EXT_COUNTDOWN_PRESET_BUNDLES,
  extCountdownBorderRadiusOptToNum,
  extCountdownPresetDigitFontFamily,
  extCountdownPresetDigitFontSize,
  extCountdownPresetSeparatorStyle,
  extCountdownPresetSuffixFontSize,
  extCountdownPresetSuffixTextColor
} from './extendedCountdownPresets';
import {
  formatTodayYmd,
  maxEndYmd,
  parseEndDateParts,
  parseEndTimestamp,
  shopDateTimeToTimestamp,
  shopPartsFromTimestamp,
  tomorrowEndTimestampString
} from './extendedCountdownTimezone';

export interface EnsureExtendedCountdownOptions {
  hasTemplateIdInUrl?: boolean;
}

export function hasNonEmptyExtendedCountdownConfig(cd?: FormCountdownVO): boolean {
  if (!cd) return false;
  return Object.keys(cd as any).length > 0;
}

export function buildDefaultExtendedCountdownFromLegacy(
  settings: PopSettingsVO,
  showTimeUnitLabels: boolean = false
): FormCountdownVO {
  const tm = (settings.styles.styleMap && settings.styles.styleMap['cartx-b-time-item']) || {};
  let fontSize = 20;
  const fontSizeRaw = tm['font-size'];
  if (fontSizeRaw != null) {
    const n = parseInt(String(fontSizeRaw).replace(/px/gi, ''), 10);
    if (!Number.isNaN(n)) fontSize = n;
  }
  let borderRadius = 4;
  const brRaw = tm['border-radius'];
  if (brRaw != null) {
    const n = parseInt(String(brRaw).replace(/px/gi, ''), 10);
    if (!Number.isNaN(n)) borderRadius = n;
  }
  let legacySec = Math.max(0, Number(settings.rules.countDown) || 0);
  if (legacySec === 0) {
    legacySec = EXT_COUNTDOWN_FIXED_DURATION_DEFAULT_SECONDS;
  }
  legacySec = Math.min(legacySec, EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS);
  const days = Math.floor(legacySec / 86400);
  const hours = Math.floor((legacySec % 86400) / 3600);
  const minutes = Math.floor((legacySec % 3600) / 60);
  const seconds = legacySec % 60;

  return {
    rules: {
      type: 'fixed_duration',
      duration: { days, hours, minutes, seconds },
      endDate: '',
      hideOnExpire: false,
      persistAcrossPages: false
    },
    styles: {
      variant: '1',
      displayParts: { showDays: false, showHMS: true, showSeconds: true },
      fontFamily: tm['font-family'] as string | undefined,
      fontSize,
      textColor: (tm['color'] as string) || '#FFFFFF',
      alignment: 'center',
      showTimeUnitLabels,
      suffixFontFamily: tm['font-family'] as string | undefined,
      suffixFontSize: 14,
      suffixTextColor: extCountdownPresetSuffixTextColor('1'),
      digitBackground: {
        enabled: true,
        color: (tm['background'] as string) || '#333333',
        borderRadius,
        borderWidth: 0,
        borderColor: '#333333'
      },
      separator: { char: ':', color: (tm['color'] as string) || '#000000' },
      spacing: { betweenDigits: 6, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 },
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
      componentBackground: ''
    }
  };
}

export function normalizeExtCountdownDurationToMinutesSecondsForRules(rules: NonNullable<FormCountdownVO['rules']>) {
  const d = rules.duration;
  if (!d) return;
  const total =
    (Number(d.days) || 0) * 86400 +
    (Number(d.hours) || 0) * 3600 +
    (Number(d.minutes) || 0) * 60 +
    (Number(d.seconds) || 0);
  const sec = Math.max(0, Math.min(EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS, total));
  Vue.set(d, 'days', 0);
  Vue.set(d, 'hours', 0);
  Vue.set(d, 'minutes', Math.floor(sec / 60));
  Vue.set(d, 'seconds', sec % 60);
}

export function applyExtFixedDurationDefaultIfZero(rules: NonNullable<FormCountdownVO['rules']>) {
  if (rules.type !== 'fixed_duration' || !rules.duration) return;
  const d = rules.duration;
  const t =
    (Number(d.days) || 0) * 86400 +
    (Number(d.hours) || 0) * 3600 +
    (Number(d.minutes) || 0) * 60 +
    (Number(d.seconds) || 0);
  if (t !== 0) return;
  Vue.set(d, 'days', 0);
  Vue.set(d, 'hours', 0);
  Vue.set(d, 'minutes', 5);
  Vue.set(d, 'seconds', 0);
}

export function syncLegacyCountDownFromExtFixedDuration(settings: PopSettingsVO) {
  const rules = settings.countdown?.rules;
  if (!rules || rules.type !== 'fixed_duration' || !rules.duration) return;
  const d = rules.duration;
  const sec = Math.min(
    EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS,
    (Number(d.minutes) || 0) * 60 + (Number(d.seconds) || 0)
  );
  Vue.set(settings.rules, 'countDown', sec);
}

/** 扩展倒计时 fontFamily 回写 styleMap，供 C 端 preLoadImageAndFont 从 styleMap 预加载 Cartx 字体 */
export const EXT_COUNTDOWN_STYLE_MAP_FONT_KEYS = [
  'cartx-b-time-item',
  'cartx-b-time-item__middle',
  'cartx-b-time-item__end'
] as const;

/** 底标字体与数字字体不一致且为 Cartx 时写入（仅预加载，C 端不参与渲染） */
export const EXT_COUNTDOWN_SUFFIX_STYLE_MAP_KEY = 'cartx-b-countdown-suffix';

export function syncExtendedCountdownFontToStyleMap(settings: PopSettingsVO) {
  if (!isEnableExtendedPopCountdown()) return;
  if (!settings?.rules?.countDownShow) return;
  const cd = settings.countdown;
  const fontFamily = String(cd?.styles?.fontFamily || '').trim();
  if (!fontFamily) return;

  if (!settings.styles) return;
  if (!settings.styles.styleMap) {
    Vue.set(settings.styles, 'styleMap', {});
  }
  const styleMap = settings.styles.styleMap;

  EXT_COUNTDOWN_STYLE_MAP_FONT_KEYS.forEach((key) => {
    if (key !== 'cartx-b-time-item' && styleMap[key] === undefined) return;
    if (!styleMap[key]) Vue.set(styleMap, key, {});
    Vue.set(styleMap[key], 'font-family', fontFamily);
  });

  const suffixFont = String(cd?.styles?.suffixFontFamily || '').trim();
  if (suffixFont && suffixFont.includes('Cartx') && suffixFont !== fontFamily) {
    if (!styleMap[EXT_COUNTDOWN_SUFFIX_STYLE_MAP_KEY]) {
      Vue.set(styleMap, EXT_COUNTDOWN_SUFFIX_STYLE_MAP_KEY, {});
    }
    Vue.set(styleMap[EXT_COUNTDOWN_SUFFIX_STYLE_MAP_KEY], 'font-family', suffixFont);
  }
}

export function applyExtFixedDateDefaultEndDateIfEmpty(rules: NonNullable<FormCountdownVO['rules']>) {
  if (rules.type !== 'fixed_date') return;
  const raw = rules.endDate;
  if (raw != null && String(raw).trim() !== '') return;
  Vue.set(rules, 'endDate', tomorrowEndTimestampString());
}

export function clampExtCountdownEndDateTimeToNowOrFuture(rules: NonNullable<FormCountdownVO['rules']>) {
  if (rules.type !== 'fixed_date') return;
  const raw = rules.endDate;
  if (raw == null || !String(raw).trim()) return;
  const ms = parseEndTimestamp(raw);
  const maxMs = shopDateTimeToTimestamp(maxEndYmd(), '23:59:59');
  let safeMs = Number.isNaN(ms) || ms < Date.now() ? Date.now() : ms;
  if (!Number.isNaN(maxMs) && safeMs > maxMs) {
    safeMs = maxMs;
  }
  if (!Number.isNaN(ms) && ms === safeMs) return;
  Vue.set(rules, 'endDate', String(safeMs));
}

export function patchExtendedCountdownReactive(cd: FormCountdownVO, settings: PopSettingsVO, defaultShowTimeUnitLabels: boolean = true) {
  if (!cd.rules) Vue.set(cd, 'rules', { type: 'fixed_duration' } as FormCountdownVO['rules']);
  if (!cd.rules.duration) Vue.set(cd.rules, 'duration', { days: 0, hours: 0, minutes: 0, seconds: 0 });
  if (cd.rules.endDate === undefined) Vue.set(cd.rules, 'endDate', '');
  if (cd.rules.hideOnExpire === undefined) Vue.set(cd.rules, 'hideOnExpire', false);
  if (cd.rules.persistAcrossPages === undefined) Vue.set(cd.rules, 'persistAcrossPages', false);
  if (!cd.styles) Vue.set(cd, 'styles', {});
  if (!cd.styles.displayParts) {
    Vue.set(cd.styles, 'displayParts', { showDays: false, showHMS: true, showSeconds: true });
  } else {
    if (cd.styles.displayParts.showDays === undefined) Vue.set(cd.styles.displayParts, 'showDays', false);
    if (cd.styles.displayParts.showHMS === undefined) Vue.set(cd.styles.displayParts, 'showHMS', true);
    if (cd.styles.displayParts.showSeconds === undefined) Vue.set(cd.styles.displayParts, 'showSeconds', true);
  }
  if (!cd.styles.digitBackground) {
    Vue.set(cd.styles, 'digitBackground', { enabled: true, color: '#333333', borderRadius: 8, borderWidth: 0, borderColor: '#333333', flipLineColor: '' });
  } else {
    if (cd.styles.digitBackground.enabled === undefined) Vue.set(cd.styles.digitBackground, 'enabled', true);
    if (cd.styles.digitBackground.borderRadius === undefined) {
      const defaultBr = String(cd.styles.variant || '1') === '3' ? 6 : 8;
      Vue.set(cd.styles.digitBackground, 'borderRadius', defaultBr);
    }
    ensureDigitBackgroundFlipLineColor(cd.styles.digitBackground);
  }
  const variantForSep = String(cd.styles.variant || '1');
  const presetSep = extCountdownPresetSeparatorStyle(variantForSep);
  if (!cd.styles.separator) {
    Vue.set(cd.styles, 'separator', { ...presetSep });
  } else {
    if (cd.styles.separator.char === undefined) Vue.set(cd.styles.separator, 'char', presetSep.char || ':');
    if (cd.styles.separator.color === undefined) Vue.set(cd.styles.separator, 'color', presetSep.color || '#000000');
    if (presetSep.fontSize != null && cd.styles.separator.fontSize === undefined) {
      Vue.set(cd.styles.separator, 'fontSize', presetSep.fontSize);
    }
    if (presetSep.bold != null && cd.styles.separator.bold === undefined) {
      Vue.set(cd.styles.separator, 'bold', presetSep.bold);
    }
  }
  if (!cd.styles.spacing) {
    Vue.set(cd.styles, 'spacing', { betweenDigits: 6, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 });
  } else if (cd.styles.spacing.marginBottom === undefined) {
    Vue.set(cd.styles.spacing, 'marginBottom', 0);
  }
  if (!cd.styles.padding) Vue.set(cd.styles, 'padding', { top: 0, bottom: 0, left: 0, right: 0 });
  const variantForDefaults = String(cd.styles.variant || '1');
  if (cd.styles.fontSize === undefined) {
    Vue.set(cd.styles, 'fontSize', extCountdownPresetDigitFontSize(variantForDefaults));
  }
  if (cd.styles.fontFamily === undefined) {
    Vue.set(cd.styles, 'fontFamily', extCountdownPresetDigitFontFamily(variantForDefaults));
  }
  if (cd.styles.alignment === undefined) Vue.set(cd.styles, 'alignment', 'center');
  if (cd.styles.showTimeUnitLabels === undefined) Vue.set(cd.styles, 'showTimeUnitLabels', defaultShowTimeUnitLabels);
  if (cd.styles.suffixFontFamily === undefined) Vue.set(cd.styles, 'suffixFontFamily', cd.styles.fontFamily || '');
  if (cd.styles.suffixFontSize === undefined) {
    Vue.set(cd.styles, 'suffixFontSize', extCountdownPresetSuffixFontSize(variantForDefaults));
  }
  if (cd.styles.suffixTextColor === undefined) {
    const v = cd.styles.variant || '1';
    Vue.set(cd.styles, 'suffixTextColor', extCountdownPresetSuffixTextColor(v));
  }
  if (cd.styles.variant === undefined || cd.styles.variant === '') Vue.set(cd.styles, 'variant', '1');
  if (cd.rules.type === 'fixed_duration') {
    normalizeExtCountdownDurationToMinutesSecondsForRules(cd.rules);
    applyExtFixedDurationDefaultIfZero(cd.rules);
    syncLegacyCountDownFromExtFixedDuration(settings);
  }
  if (cd.rules.type === 'fixed_date') {
    applyExtFixedDateDefaultEndDateIfEmpty(cd.rules);
    clampExtCountdownEndDateTimeToNowOrFuture(cd.rules);
  }
}

export function ensureExtendedCountdownIfNeeded(settings: PopSettingsVO, opts: EnsureExtendedCountdownOptions = {}) {
  if (!isEnableExtendedPopCountdown()) return;
  if (!settings.rules.countDownShow) return;
  const isNewCountdownTemplate = hasNonEmptyExtendedCountdownConfig(settings.countdown);
  const defaultShowTimeUnitLabels = !!opts.hasTemplateIdInUrl || isNewCountdownTemplate;
  const isFreshExtendedCountdown = !settings.countdown;
  if (isFreshExtendedCountdown) {
    Vue.set(settings, 'countdown', buildDefaultExtendedCountdownFromLegacy(settings, defaultShowTimeUnitLabels));
  }
  patchExtendedCountdownReactive(settings.countdown!, settings, defaultShowTimeUnitLabels);
  syncExtendedCountdownFontToStyleMap(settings);
  // 新建时 variant 默认为预设 1，但 buildDefault 仍沿用旧版 styleMap 颜色；需同步预设配色到预览
  if (isFreshExtendedCountdown) {
    applyExtendedCountdownPreset(settings, '1');
  }
}

export function getExtFixedDurationMinutesMax(cd?: FormCountdownVO): number {
  const d = cd?.rules?.duration;
  const s = Number(d?.seconds) || 0;
  return Math.min(60, Math.floor((EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS - s) / 60));
}

export function getExtFixedDurationSecondsMax(cd?: FormCountdownVO): number {
  const d = cd?.rules?.duration;
  const m = Number(d?.minutes) || 0;
  const cap = EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS - m * 60;
  if (cap <= 0) return 0;
  return Math.min(59, cap);
}

export function applyExtendedCountdownPreset(settings: PopSettingsVO, id: string) {
  const cd = settings.countdown;
  if (!cd) return;
  const b = EXT_COUNTDOWN_PRESET_BUNDLES[id];
  if (!b) return;
  patchExtendedCountdownReactive(cd, settings);
  Vue.set(cd.styles, 'variant', b.variant);
  Vue.set(cd.styles, 'textColor', b.textColor);
  /* 数字字号/字体走配置默认（extCountdownPreset*），不写入 preset bundle，与预设缩略图 SCSS 解耦 */
  Vue.set(cd.styles, 'fontSize', extCountdownPresetDigitFontSize(id));
  const digitFamily = extCountdownPresetDigitFontFamily(id);
  if (digitFamily) {
    Vue.set(cd.styles, 'fontFamily', digitFamily);
    Vue.set(cd.styles, 'suffixFontFamily', digitFamily);
  }
  Vue.set(cd.styles, 'showTimeUnitLabels', b.showTimeUnitLabels);
  Vue.set(cd.styles, 'suffixFontSize', b.suffixFontSize);
  Vue.set(cd.styles, 'suffixTextColor', b.suffixTextColor);
  Vue.set(cd.styles, 'digitBackground', {
    ...(cd.styles.digitBackground || {}),
    ...b.digitBackground
  });
  const sep = { ...(cd.styles.separator || {}), char: b.separator.char, color: b.separator.color };
  if (b.separator.fontSize != null) sep.fontSize = b.separator.fontSize;
  else Vue.delete(sep, 'fontSize');
  if (b.separator.bold != null) sep.bold = b.separator.bold;
  else Vue.delete(sep, 'bold');
  Vue.set(cd.styles, 'separator', sep);
  syncExtendedCountdownFontToStyleMap(settings);
  // 预设 3：数字块间距设计稿 6px
  if (id === '3') {
    if (!cd.styles.spacing) {
      Vue.set(cd.styles, 'spacing', { betweenDigits: 6, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 });
    } else {
      Vue.set(cd.styles.spacing, 'betweenDigits', 6);
    }
  }
}

export function getExtCountdownDigitBorderRadiusSelectValue(
  cd: FormCountdownVO | undefined,
  borderRadiusOptions: { value: string; name: string }[]
): string {
  const raw = cd?.styles?.digitBackground?.borderRadius;
  const variant = String(cd?.styles?.variant || '1');
  const n = Number(raw);
  if (raw === undefined || raw === null || Number.isNaN(n)) {
    return variant === '3' ? '6px' : '4px';
  }
  if (n >= 500) return '1000px';
  for (const o of borderRadiusOptions) {
    if (extCountdownBorderRadiusOptToNum(o.value) === n) return o.value;
  }
  return `${n}px`;
}

export function getExtCountdownEndDatePickerOptions() {
  return {
    disabledDate: (time: Date) => {
      const y = time.getFullYear();
      const m = `${time.getMonth() + 1}`.padStart(2, '0');
      const d = `${time.getDate()}`.padStart(2, '0');
      const cur = `${y}-${m}-${d}`;
      return cur < formatTodayYmd() || cur > maxEndYmd();
    }
  };
}

export function getExtCountdownEndTimePickerOptions(endDateYmd: string) {
  const todayStr = formatTodayYmd();
  const fromNowTime = shopPartsFromTimestamp(Date.now()).time;
  if (endDateYmd && endDateYmd > todayStr) {
    return { selectableRange: '00:00:00 - 23:59:59' };
  }
  return { selectableRange: `${fromNowTime} - 23:59:59` };
}

/** 组件外层背景色非空时，上下默认内边距（与 C 端 adaptSettingsToCartxCountdown 一致） */
export const EXT_COUNTDOWN_COMPONENT_BG_VERTICAL_PADDING_PX = 10;

/** 解析颜色字符串 alpha，无法识别时非空视为 1 */
export function parseExtCountdownColorAlpha(color?: string | null): number {
  const raw = String(color || '').trim();
  if (!raw) return 0;
  const lower = raw.toLowerCase();
  if (lower === 'transparent') return 0;

  const rgbaMatch = lower.match(
    /^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)$/
  );
  if (rgbaMatch) {
    const a = parseFloat(rgbaMatch[1]);
    return Number.isFinite(a) ? Math.max(0, Math.min(1, a)) : 0;
  }

  if (lower.startsWith('#')) {
    const hex = lower.slice(1);
    if (hex.length === 8) return parseInt(hex.slice(6, 8), 16) / 255;
    if (hex.length === 4) {
      const a = hex[3];
      return parseInt(`${a}${a}`, 16) / 255;
    }
    return 1;
  }

  return 1;
}

export function hasExtCountdownComponentBackground(bg?: string | null): boolean {
  const lower = String(bg || '').trim().toLowerCase();
  if (!lower || lower === 'transparent') return false;
  return parseExtCountdownColorAlpha(bg) > 0;
}

/** 仅在有有效外层背景色时，为 .cartx-b-time-extended 上下各加 10px（可与 styles.padding.top/bottom 叠加） */
export function extendedCountdownVerticalPadding(
  componentBackground?: string | null,
  pad?: { top?: number; bottom?: number }
): { paddingTop?: string; paddingBottom?: string } {
  if (!hasExtCountdownComponentBackground(componentBackground)) {
    return { paddingTop: undefined, paddingBottom: undefined };
  }
  const top = Number(pad?.top ?? 0) + EXT_COUNTDOWN_COMPONENT_BG_VERTICAL_PADDING_PX;
  const bottom = Number(pad?.bottom ?? 0) + EXT_COUNTDOWN_COMPONENT_BG_VERTICAL_PADDING_PX;
  return {
    paddingTop: `${top}px`,
    paddingBottom: `${bottom}px`
  };
}

/** 配置区上下外边距：挂在 .cartx-b-time-extended 的 margin（预览区父级为 flex 列，避免与上文 margin 折叠） */
export function extendedCountdownVerticalMargin(
  sp?: { marginTop?: number; marginBottom?: number }
): { marginTop?: string; marginBottom?: string } {
  const top = Math.max(0, Number(sp?.marginTop ?? 0));
  const bottom = Math.max(0, Number(sp?.marginBottom ?? 0));
  return {
    marginTop: top > 0 ? `${top}px` : undefined,
    marginBottom: bottom > 0 ? `${bottom}px` : undefined
  };
}

/**
 * 配置区左右布局（与 C 端 adaptSettingsToCartxCountdown 一致）：
 * - spacing.marginLeft/Right：块本身左右收缩（width calc + margin），不影响兄弟元素占位宽度
 * - padding.left/right：块内边距，在收缩后的块内再留白
 */
export function extendedCountdownHorizontalPadding(
  pad?: { left?: number; right?: number },
  sp?: { marginLeft?: number; marginRight?: number }
): {
  marginLeft?: string;
  marginRight?: string;
  width?: string;
  paddingLeft?: string;
  paddingRight?: string;
} {
  const insetLeft = Math.max(0, Number(sp?.marginLeft ?? 0));
  const insetRight = Math.max(0, Number(sp?.marginRight ?? 0));
  const padLeft = Math.max(0, Number(pad?.left ?? 0));
  const padRight = Math.max(0, Number(pad?.right ?? 0));
  const out: {
    marginLeft?: string;
    marginRight?: string;
    width?: string;
    paddingLeft?: string;
    paddingRight?: string;
  } = {};
  if (insetLeft > 0) out.marginLeft = `${insetLeft}px`;
  if (insetRight > 0) out.marginRight = `${insetRight}px`;
  if (insetLeft > 0 || insetRight > 0) {
    out.width = `calc(100% - ${insetLeft + insetRight}px)`;
  }
  if (padLeft > 0) out.paddingLeft = `${padLeft}px`;
  if (padRight > 0) out.paddingRight = `${padRight}px`;
  return out;
}

